import { apiRequest } from '../lib/api-client';
import type { BadgeTone } from '../ui/Page';

/** Social accounts and the post calendar. */

export const NETWORKS = ['LOCAL', 'INSTAGRAM', 'FACEBOOK', 'LINKEDIN'] as const;
export type Network = (typeof NETWORKS)[number];

export const POST_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SCHEDULED',
  'PUBLISHING',
  'PUBLISHED',
  'FAILED',
  'CANCELED',
] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export interface SocialAccount {
  id: string;
  network: Network;
  name: string;
  externalId: string | null;
  status: 'CONNECTED' | 'EXPIRED' | 'REVOKED' | 'DISABLED';
  statusDetail: string | null;
  tokenExpiresAt: string | null;
  requiresApproval: boolean;
  createdAt: string;
}

export interface SocialPost {
  id: string;
  accountId: string;
  network: Network;
  accountName: string;
  body: string;
  mediaFileIds: string[];
  linkUrl: string | null;
  status: PostStatus;
  scheduledAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  publishedAt: string | null;
  externalUrl: string | null;
  failureReason: string | null;
  authorType: 'USER' | 'AI_AGENT';
  ownerId: string | null;
  createdAt: string;
}

export interface PostMetrics {
  capturedAt: string;
  impressions: number | null;
  reach: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  clicks: number | null;
}

export interface Schedule {
  date: string;
  time: string;
}

export function listAccounts(): Promise<{ accounts: SocialAccount[] }> {
  return apiRequest('/v1/social/accounts');
}

export function connectAccount(input: {
  network: Network;
  name: string;
  externalId?: string | null;
  config?: Record<string, unknown>;
  requiresApproval?: boolean;
}): Promise<SocialAccount> {
  return apiRequest('/v1/social/accounts', { method: 'POST', body: input });
}

export function setRequiresApproval(id: string, requiresApproval: boolean): Promise<SocialAccount> {
  return apiRequest(`/v1/social/accounts/${id}`, { method: 'PATCH', body: { requiresApproval } });
}

export function disconnectAccount(id: string): Promise<void> {
  return apiRequest(`/v1/social/accounts/${id}`, { method: 'DELETE' });
}

export function listPosts(filters: {
  from?: string;
  to?: string;
  status?: PostStatus[];
  accountId?: string;
}): Promise<{ posts: SocialPost[] }> {
  const params = new URLSearchParams({ limit: '500' });

  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.accountId) params.set('accountId', filters.accountId);
  for (const status of filters.status ?? []) params.append('status', status);

  return apiRequest(`/v1/social/posts?${params.toString()}`);
}

export function getPost(id: string): Promise<SocialPost & { metrics: PostMetrics | null }> {
  return apiRequest(`/v1/social/posts/${id}`);
}

export interface ComposeInput {
  accountId: string;
  body: string;
  mediaFileIds?: string[];
  linkUrl?: string | null;
  schedule?: Schedule | null;
}

export function composePost(input: ComposeInput): Promise<SocialPost> {
  return apiRequest('/v1/social/posts', { method: 'POST', body: input });
}

export function editPost(
  id: string,
  input: Partial<Omit<ComposeInput, 'accountId'>>,
): Promise<SocialPost> {
  return apiRequest(`/v1/social/posts/${id}`, { method: 'PATCH', body: input });
}

export function approvePost(id: string): Promise<SocialPost> {
  return apiRequest(`/v1/social/posts/${id}/approve`, { method: 'POST' });
}

export function cancelPost(id: string): Promise<SocialPost> {
  return apiRequest(`/v1/social/posts/${id}/cancel`, { method: 'POST' });
}

export function deletePost(id: string): Promise<void> {
  return apiRequest(`/v1/social/posts/${id}`, { method: 'DELETE' });
}

export function postTone(status: PostStatus): BadgeTone {
  switch (status) {
    case 'PUBLISHED':
      return 'positive';
    case 'FAILED':
      return 'danger';
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'SCHEDULED':
    case 'PUBLISHING':
      return 'info';
    default:
      return 'neutral';
  }
}

/** What the network allows; mirrored from the API so the editor can say so before the 422. */
export const NETWORK_LIMITS: Record<Network, { maxChars: number; requiresMedia: boolean }> = {
  LOCAL: { maxChars: 5000, requiresMedia: false },
  INSTAGRAM: { maxChars: 2200, requiresMedia: true },
  FACEBOOK: { maxChars: 63_206, requiresMedia: false },
  LINKEDIN: { maxChars: 3000, requiresMedia: false },
};
